import { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import { toast } from "sonner";
import { LOGO } from "@/lib/api";
import { onInstallAvailable, promptInstall, isAppInstalled } from "@/lib/install";

const DISMISS_KEY = "btm_install_dismissed_v2";
const REMIND_DAYS = 14;

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    if (isAppInstalled()) return;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until && Date.now() < until) return;

    // One-tap install becomes available when the browser offers it
    const off = onInstallAvailable((e) => setDeferred(e));

    // Card shows on every website open for non-installed visitors
    const t = setTimeout(() => setVisible(true), 2000);
    return () => {
      clearTimeout(t);
      off();
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      const outcome = await promptInstall();
      setVisible(false);
      if (outcome !== "accepted") dismiss();
      return;
    }
    if (ios) return; // iOS card shows steps instead of this button
    toast.info("Already installed? Find BTMEDU in your app drawer — or use your browser menu → Install app.");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-[90] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm" data-testid="install-prompt">
      <div className="bg-navy text-white rounded-3xl shadow-2xl shadow-navy/40 border border-white/10 p-5 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-brand/20 blur-2xl pointer-events-none" />
        <button onClick={dismiss} className="absolute top-3 right-3 p-1.5 text-white/50 hover:text-white" aria-label="Close install prompt" data-testid="install-dismiss">
          <X size={16} />
        </button>
        <div className="flex items-center gap-3.5">
          <span className="bg-white rounded-2xl p-2 shrink-0">
            <img src={LOGO} alt="BTMEDU" className="h-8 w-auto" />
          </span>
          <div>
            <p className="font-display font-bold text-sm">Install BTMEDU App</p>
            <p className="text-xs text-white/60 mt-0.5">Faster shopping, one tap from your home screen.</p>
          </div>
        </div>
        {ios ? (
          <div className="mt-4 text-xs text-white/80 space-y-1.5" data-testid="install-steps">
            <p className="flex items-center gap-2">1. Tap <Share size={13} className="text-brand" /> <span className="font-bold">Share</span> in Safari</p>
            <p className="flex items-center gap-2">2. Choose <Plus size={13} className="text-brand" /> <span className="font-bold">Add to Home Screen</span></p>
            <p className="flex items-center gap-2">3. Tap <span className="font-bold">Add</span> — done</p>
          </div>
        ) : (
          <button onClick={install} className="mt-4 w-full h-11 rounded-full bg-brand hover:bg-brand-accent text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors" data-testid="install-accept">
            <Download size={15} /> Install App
          </button>
        )}
      </div>
    </div>
  );
}
